import { useEffect, useState } from "react";
import { Crown, DollarSign, Users, Ticket, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, StatCard, TH, TD, Badge } from "./AdminLayout";

const inputCls = "bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple";

function PromoCodes() {
  const [promos, setPromos] = useState(null);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(10);
  const [duration, setDuration] = useState("once");
  const [maxUses, setMaxUses] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/promo-codes").then((r) => setPromos(r.data)).catch(() => setPromos([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", {
        code, percent_off: Number(percent), duration,
        max_redemptions: maxUses ? Number(maxUses) : null,
      });
      toast.success(`Code ${code.toUpperCase()} created`);
      setCode(""); setPercent(10); setMaxUses("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create code"); }
    finally { setBusy(false); }
  };

  const deactivate = async (id, c) => {
    try {
      await api.delete(`/admin/promo-codes/${id}`);
      toast.success(`${c} deactivated`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="rounded-lg border border-white/5 bg-panel p-6 mb-8" data-testid="promo-codes-card">
      <div className="flex items-center gap-2.5 mb-1.5">
        <Ticket size={16} className="text-blurple" />
        <h3 className="font-heading font-bold text-white">Discount codes</h3>
      </div>
      <p className="text-xs text-white/40 mb-5">Customers enter these at the Stripe checkout. Codes apply to Premium subscriptions.</p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODE" maxLength={24}
          className={`${inputCls} font-mono w-40 uppercase`} data-testid="promo-code-input" />
        <div className="flex items-center gap-1.5">
          <input type="number" min="1" max="100" value={percent} onChange={(e) => setPercent(e.target.value)}
            className={`${inputCls} w-20`} data-testid="promo-percent-input" />
          <span className="text-xs text-white/40">% off</span>
        </div>
        <select value={duration} onChange={(e) => setDuration(e.target.value)} className={`${inputCls} [&>option]:bg-panel`} data-testid="promo-duration-select">
          <option value="once">First month only</option>
          <option value="forever">Every month</option>
        </select>
        <input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Max uses (optional)"
          className={`${inputCls} w-40`} data-testid="promo-max-input" />
        <button onClick={create} disabled={busy || code.trim().length < 3} data-testid="promo-create-button"
          className="inline-flex items-center gap-1.5 bg-blurple hover:bg-[#4752c4] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-150">
          <Plus size={14} /> Create code
        </button>
      </div>
      {promos === null ? (
        <p className="text-white/40 font-mono text-sm">Loading…</p>
      ) : promos.length === 0 ? (
        <p className="text-white/35 text-sm">No codes yet.</p>
      ) : (
        <div className="space-y-2" data-testid="promo-codes-list">
          {promos.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-md border border-white/10 px-4 py-2.5 text-sm" data-testid={`promo-row-${p.code}`}>
              <span className="font-mono font-bold text-white">{p.code}</span>
              <Badge tone="cyan">{p.percent_off}% off · {p.duration === "forever" ? "every month" : "first month"}</Badge>
              <span className="text-xs text-white/40">{p.times_redeemed}{p.max_redemptions ? `/${p.max_redemptions}` : ""} used</span>
              <span className={`ml-auto text-xs ${p.active ? "text-emerald-400" : "text-white/30"}`}>{p.active ? "active" : "inactive"}</span>
              {p.active && (
                <button onClick={() => deactivate(p.id, p.code)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`promo-deactivate-${p.code}`} aria-label={`Deactivate ${p.code}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PremiumAdmin() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/premium").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/premium/subs/${id}`, { status });
      toast.success(`Subscription ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p className="text-white/40 font-mono text-sm">Loading…</p>;

  const active = data.subscribers.filter((s) => s.status === "active");

  return (
    <div data-testid="admin-premium">
      <PageTitle title="Premium & Payments" sub="Plans, subscribers and revenue.">
        <button onClick={async () => { try { const r = await api.post("/admin/premium/sync"); toast.success(`Synced with Stripe — ${r.data.deactivated} deactivated`); load(); } catch (e) { toast.error("Sync failed"); } }} data-testid="premium-sync-button"
          className="inline-flex items-center gap-2 text-xs font-semibold border border-white/15 hover:border-blurple/50 hover:text-white text-white/60 rounded-full px-4 py-2 transition-colors duration-150">
          Sync with Stripe
        </button>
      </PageTitle>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="MRR" value={`$${data.mrr.toFixed(2)}`} icon={DollarSign} tone="text-emerald-400" testId="premium-mrr" />
        <StatCard label="Active subs" value={active.length} icon={Crown} tone="text-amber-400" testId="premium-active" />
        <StatCard label="Total subs" value={data.subscribers.length} icon={Users} testId="premium-total" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="premium-plans-admin">
        {data.plans.map((p) => (
          <div key={p.id} className="rounded-lg border border-white/5 bg-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-white">{p.name}</h3>
              <div className="flex items-center gap-1.5">
                <span className="text-white/40 text-sm">$</span>
                <input type="number" step="0.01" defaultValue={p.price} onBlur={async (e) => { const v = Number(e.target.value); if (v !== p.price) { await api.patch(`/admin/premium/plans/${p.id}`, { price: v }); toast.success("Price updated"); load(); } }}
                  className="w-20 bg-white/5 border border-white/10 rounded-md px-2 py-1 text-sm font-mono text-white focus:outline-none focus:ring-2 focus:ring-blurple" data-testid={`plan-price-${p.key}`} />
              </div>
            </div>
            <ul className="space-y-1.5">
              {p.features.map((f, i) => <li key={i} className="text-xs text-white/55">· {f}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <PromoCodes />

      <div className="rounded-lg border border-white/5 bg-panel overflow-x-auto" data-testid="premium-subs-table">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-white/5"><tr><TH>User</TH><TH>Server</TH><TH>Plan</TH><TH>Since</TH><TH>Status</TH><TH /></tr></thead>
          <tbody className="divide-y divide-white/5">
            {data.subscribers.map((s) => (
              <tr key={s.id} data-testid={`sub-row-${s.user}`}>
                <TD className="font-semibold text-white">{s.user}</TD>
                <TD>{s.guild}</TD>
                <TD><Badge tone="amber">{s.plan}</Badge></TD>
                <TD className="font-mono text-xs text-white/50">{new Date(s.since).toLocaleDateString()}</TD>
                <TD><Badge tone={s.status === "active" ? "green" : s.status === "refunded" ? "cyan" : "grey"}>{s.status}</Badge></TD>
                <TD>
                  {s.status === "active" && (
                    <div className="flex gap-2">
                      <button onClick={() => setStatus(s.id, "cancelled")} className="text-xs font-semibold text-white/50 border border-white/15 rounded-full px-3 py-1 hover:text-white transition-colors duration-150" data-testid={`sub-cancel-${s.id}`}>Cancel</button>
                      <button onClick={() => setStatus(s.id, "refunded")} className="text-xs font-semibold text-volt border border-volt/30 rounded-full px-3 py-1 hover:bg-volt/10 transition-colors duration-150" data-testid={`sub-refund-${s.id}`}>Refund</button>
                    </div>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
